/**
 * PO Lines Filters Panel — routes to the right filter UI by portal mode.
 *
 * CR-6348: the Supplier View gets the reworked sectioned layout
 * (SupplierSOLinesFilters). CR-6347: the Customer View now gets its own
 * sectioned layout (CustomerSectionedFilters) whose header stays visible when
 * the sections are collapsed and carries the Refresh / Export controls.
 *
 * The panel is always mounted by the parent; the supplier path renders nothing
 * when filters are hidden (preserving the old toolbar-toggle behaviour), while
 * the customer path always renders its header.
 */
import { POLinesFilters, FilterOptions } from './types';
import { usePortalMode } from '../../../../hooks/usePortalMode';
import { CustomerSectionedFilters } from './filters/CustomerSectionedFilters';
import { SupplierSOLinesFilters } from './filters/SupplierSOLinesFilters';

interface POLinesFiltersPanelProps {
  filters: POLinesFilters;
  filterOptions: FilterOptions | null;
  onFilterChange: (key: string, value: string) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  onRefresh: () => void;
  onExport: (format: 'xlsx' | 'csv') => void;
  exporting: boolean;
}

export function POLinesFiltersPanel({
  filters,
  filterOptions,
  onFilterChange,
  onSearch,
  onClearFilters,
  showFilters,
  onToggleFilters,
  onRefresh,
  onExport,
  exporting,
}: POLinesFiltersPanelProps) {
  const { isSupplier } = usePortalMode();

  if (isSupplier) {
    // Supplier view unchanged: the whole panel toggles as one unit, driven by
    // the results-toolbar Filters button.
    if (!showFilters) return null;
    return (
      <SupplierSOLinesFilters
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
        onSearch={onSearch}
        onClearFilters={onClearFilters}
        onHideFilters={onToggleFilters}
      />
    );
  }

  return (
    <CustomerSectionedFilters
      filters={filters}
      filterOptions={filterOptions}
      onFilterChange={onFilterChange}
      onSearch={onSearch}
      onClearFilters={onClearFilters}
      showFilters={showFilters}
      onToggleFilters={onToggleFilters}
      onRefresh={onRefresh}
      onExport={onExport}
      exporting={exporting}
    />
  );
}
